/**
 * Menu configuration for the sidebar navigation.
 */

import {
  ArrowRightLeft,
  BarChart2,
  Bell,
  BookOpen,
  BookOpenText,
  Bot,
  ChartLine,
  ChartNoAxesCombined,
  Clapperboard,
  ClipboardList,
  Code,
  Database,
  Folder,
  Globe,
  House,
  MessagesSquare,
  Phone,
  Shield,
  SlidersHorizontal,
  Sparkles,
  SquarePlay,
  Star,
  Tags,
  UserCog,
  UserRoundCog,
  Users,
  WalletCards,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { isBeforeDate } from "@/lib/utils/safe-date";

export const LABELS_NEW_BADGE = { badge: "LAUNCH_BAR.BADGE", badgeUntil: "2026-11-09" } as const;

export interface MenuItem {
  id: string;
  title: string;
  icon: LucideIcon;
  path?: string;
  badge?: string;
  badgeUntil?: string;
  external?: boolean;
  externalUrl?: string;
  action?: string;
  requiresPermission?: string;
  requiresFeature?: string;
  /** When the feature flag is off, show the item as a locked upsell instead of hiding it. */
  lockedFeature?: string;
  /** i18n key for the upsell tooltip/dialog body shown when the item is locked. */
  lockedDescriptionKey?: string;
  isMobileOnly?: boolean;
  /** Can be disabled for free trial users with no uploads */
  disableOnFreeTrial?: boolean;
  children?: MenuItem[];
}

export function activeBadge(item: Pick<MenuItem, "badge" | "badgeUntil">): string | undefined {
  if (item.badgeUntil && !isBeforeDate(item.badgeUntil)) return undefined;
  return item.badge;
}

export interface SettingsMenuItem {
  href: string;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  /** Key in user.permission.profileSettings; the item hides when that key is false. */
  permissionKey?: string;
  badge?: string;
  badgeUntil?: string;
}

export interface SettingsMenuGroup {
  labelKey: string;
  items: SettingsMenuItem[];
}

export interface MenuSection {
  id: string;
  label?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  requiresPermission?: string;
  requiresFeature?: string;
  isMobileOnly?: boolean;
  items: MenuItem[];
}

/**
 * Primary navigation items — always visible at top of sidebar.
 */
export const primaryMenuItems: MenuItem[] = [
  {
    id: "home",
    title: "Home",
    icon: House,
    path: "/home",
  },
  {
    id: "dashboards",
    title: "Dashboards",
    icon: ChartNoAxesCombined,
    path: "/dashboards",
    // Shown as a locked upsell when the flag is off; hidden only if the feature doesn't exist yet.
    lockedFeature: "dashboards.enabled",
    lockedDescriptionKey: "DASHBOARDS.UPSELL.DESC",
  },
  {
    id: "explore",
    title: "Analyze Data",
    icon: ChartLine,
    path: "/explore",
    disableOnFreeTrial: true,
  },
  {
    id: "meeting-assistant",
    title: "Meeting Assistant",
    icon: Bot,
    path: "/meeting-assistant",
  },
  {
    id: "chat",
    title: "Chat History",
    icon: Sparkles,
    path: "/chat/history",
    disableOnFreeTrial: true,
  },
  {
    id: "favorites",
    title: "Favorites",
    icon: Star,
    path: "/favorites",
    disableOnFreeTrial: true,
  },
];

/**
 * Sidebar sections — collapsible groups below the primary items.
 */
export const menuSections: MenuSection[] = [
  {
    id: "folders",
    label: "Folders",
    collapsible: false,
    defaultExpanded: true,
    items: [
      {
        id: "folders-root",
        title: "Folders",
        icon: Folder,
        path: "/folder",
        action: "createFolder",
      },
    ],
  },
  {
    id: "content",
    label: "Content",
    collapsible: true,
    defaultExpanded: true,
    items: [
      {
        id: "recorder",
        title: "Surveys",
        icon: ClipboardList,
        path: "/recorder",
      },
      {
        id: "embed-media",
        title: "Media",
        icon: SquarePlay,
        path: "/embed-media",
      },
      {
        id: "clips",
        title: "Clips",
        icon: Clapperboard,
        path: "/clips",
        disableOnFreeTrial: true,
      },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    collapsible: true,
    defaultExpanded: true,
    requiresFeature: "voiceAgent.enabled",
    items: [
      { id: "agents-list", title: "Agents", icon: Bot, path: "/agents/list" },
      { id: "agents-conversations", title: "Conversations", icon: MessagesSquare, path: "/agents/conversations" },
      { id: "agents-phone-numbers", title: "Phone Numbers", icon: Phone, path: "/agents/phone-numbers" },
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    collapsible: true,
    defaultExpanded: false,
    items: [
      {
        id: "knowledge-base",
        title: "Knowledge Base",
        icon: BookOpen,
        path: "/knowledge-base",
        requiresPermission: "knowledgeBase",
      },
      {
        id: "automations",
        title: "Automations",
        icon: Zap,
        path: "/automations",
      },
      {
        id: "integrations",
        title: "Integrations",
        icon: Workflow,
        path: "/integrations",
      },
      {
        id: "team-manage",
        title: "Team",
        icon: Users,
        path: "/team",
        requiresPermission: "team",
      },
      {
        id: "developers-manage",
        title: "Developers",
        icon: Code,
        path: "/developers",
        requiresPermission: "developers",
        requiresFeature: "developers.apiKeys",
      },
      {
        id: "ai-assistant",
        title: "AI Assistant",
        icon: Sparkles,
        path: "/profile/ai-assistant",
      },
      {
        id: "fields",
        title: "Fields",
        icon: ArrowRightLeft,
        path: "/profile/fields",
      },
      {
        id: "labels",
        title: "Labels",
        icon: Tags,
        path: "/profile/labels",
        ...LABELS_NEW_BADGE,
      },
    ],
  },
];

/**
 * Settings navigation shown in the main sidebar on /profile routes.
 */
export const settingsMenuGroups: SettingsMenuGroup[] = [
  {
    labelKey: "PROFILE.NAV_GROUP_ACCOUNT",
    items: [
      { href: "/profile", labelKey: "PROFILE.TITLE", labelFallback: "Profile", icon: UserRoundCog },
      { href: "/profile/security", labelKey: "PROFILE.SECURITY.DOCUMENT_TITLE", labelFallback: "Security", icon: Shield },
      { href: "/profile/preferences", labelKey: "PROFILE.PREFERENCES_LABEL", labelFallback: "Preferences", icon: UserCog, permissionKey: "accountPreferences" },
      { href: "/profile/notifications", labelKey: "NOTIFICATIONS.TITLE", labelFallback: "Notifications", icon: Bell },
    ],
  },
  {
    labelKey: "PROFILE.NAV_GROUP_BILLING",
    items: [
      { href: "/profile/usage", labelKey: "PROFILE.USAGE.LABEL", labelFallback: "Plan & Usage", icon: BarChart2 },
      { href: "/profile/payment", labelKey: "PROFILE.PAYMENT_SETTINGS", labelFallback: "Payment & Invoices", icon: WalletCards },
    ],
  },
  {
    labelKey: "PROFILE.NAV_GROUP_WORKSPACE",
    items: [
      { href: "/profile/ai-assistant", labelKey: "AI.AI_ASSISTANT", labelFallback: "AI Assistant", icon: Sparkles },
      { href: "/profile/customization", labelKey: "PROFILE.PLAYER_BRANDING", labelFallback: "Player Branding", icon: SlidersHorizontal, permissionKey: "accountCustomization" },
      { href: "/profile/domains", labelKey: "PROFILE.CUSTOM_DOMAINS", labelFallback: "Custom Domains", icon: Globe },
      { href: "/profile/vocabulary", labelKey: "VOCABULARY.TITLE", labelFallback: "Vocabulary", icon: BookOpenText },
      { href: "/profile/fields", labelKey: "PROFILE.FIELDS", labelFallback: "Fields", icon: ArrowRightLeft },
      { href: "/profile/labels", labelKey: "PROFILE.LABELS_TITLE", labelFallback: "Labels", icon: Tags, ...LABELS_NEW_BADGE },
    ],
  },
  {
    labelKey: "",
    items: [
      { href: "/profile/data", labelKey: "PROFILE.DATA_MANAGEMENT", labelFallback: "Data Management", icon: Database, permissionKey: "dataManagement" },
    ],
  },
];

/** Drops settings items whose profileSettings permission is false, then empty groups. */
export function filterSettingsGroups(
  groups: SettingsMenuGroup[],
  profileSettings: Record<string, boolean> | undefined,
): SettingsMenuGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (!item.permissionKey || !profileSettings) return true;
        return profileSettings[item.permissionKey] !== false;
      }),
    }))
    .filter((group) => group.items.length > 0);
}
